import { Core } from "@faire/design-tokens";
import { RetailerOnboardingProgress } from "@faire/web-api/faire/retailers/pos/RetailerOnboardingProgress";
import { FreeShippingReason } from "@faire/web-api/indigofair/data/FreeShippingReason";
import { IBrandDiscount } from "@faire/web-api/indigofair/data/IBrandDiscount";
import { IFDMarketplaceDiscountData } from "@faire/web-api/indigofair/data/IFDMarketplaceDiscountData";
import { IProductMessageByViewport } from "@faire/web-api/indigofair/retailer/IProductMessageByViewport";
import { IRetailerPosLinkSummary } from "@faire/web-api/indigofair/retailers/pos/IRetailerPosLinkSummary";
import { RetailerLinkState } from "@faire/web-api/indigofair/retailers/pos/RetailerLinkState";
import posGetSummary from "@faire/web-api/retailers-pos-api/summary/get";
import { route as amexRoute } from "@faire/web-api/routes/amex";
import { route as brandApplicationRoute } from "@faire/web-api/routes/apply";
import { route as brandAdApplicationRoute } from "@faire/web-api/routes/apply/ad";
import { route as brandInvoiceTokenRoute } from "@faire/web-api/routes/brand-invoice/brandInvoiceToken";
import { route as brandPageRoute } from "@faire/web-api/routes/brand/brandToken";
import { route as messagesRoute } from "@faire/web-api/routes/messages";
import { route as messagesWithOtherPartyRoute } from "@faire/web-api/routes/messages/otherPartyToken";
import { route as posConsentRoute } from "@faire/web-api/routes/point-of-sale/consent";
import { route as collectionRoute } from "@faire/web-api/routes/product-based-collection/product_based_collection_token";
import { route as productRoute } from "@faire/web-api/routes/product/productToken";
import { route as brandReferralRoute } from "@faire/web-api/routes/user/brand-referrals";
import { route as brandReferralsRoute } from "@faire/web-api/routes/user/brand-referrals";
import { clock } from "@faire/web/common/clock";
import { getGlobalProperty } from "@faire/web/common/globals/getGlobalProperty";
import { getLocationOrThrow } from "@faire/web/common/globals/getLocation";
import { makeObservable } from "@faire/web/common/makeObservable";
import { getCurrentPath } from "@faire/web/common/Path";
import { parseQueryParams } from "@faire/web/common/query-params/parseQueryParams";
import { pathMatchesRoute } from "@faire/web/common/routes/RouteMatcher";
import { dangerouslyGetIsSSR } from "@faire/web/common/server-side-rendering/dangerouslyGetIsSSR";
import { singletonGetter } from "@faire/web/common/singletons/getter";
import { isNotUndefined } from "@faire/web/common/typescriptUtils";
import Viewport from "@faire/web/common/Viewport";
import { createStoreHook } from "@faire/web/ui/hooks/useStore";
import { matchPathWithLocale } from "@faire/web/ui/routing/react-utils";
import differenceInDays from "date-fns/differenceInDays";
import isBefore from "date-fns/isBefore";
import compact from "lodash/compact";
import isUndefined from "lodash/isUndefined";
import some from "lodash/some";
import {
  _allowStateChangesInsideComputed,
  action,
  computed,
  observable,
} from "mobx";
import { lazyObservable } from "mobx-utils";

import {
  LOGGED_IN_PROMO_BANNERS,
  LOGGED_OUT_PROMO_BANNERS,
  PromoBannerType,
} from "@faire/retailer/components/Notifications/PromoBanner/bannerOrder";
import { hasFaireDirectFirstOrderIncentive } from "@faire/retailer/hooks/FaireDirect/useFaireDirectFirstOrderIncentive/shared/hasFaireDirectFirstOrderIncentive";
import { parseFaireDirectFirstOrderIncentiveFromBrandDiscounts } from "@faire/retailer/hooks/FaireDirect/useFaireDirectFirstOrderIncentive/shared/parseFaireDirectFirstOrderIncentiveFromBrandDiscounts";
import { hasFaireDirectMarketplaceIncentive } from "@faire/retailer/hooks/FaireDirect/useFaireDirectMarketplaceIncentive/shared/hasFaireDirectMarketplaceIncentive";
import { setIntervalIfPossible } from "@faire/retailer/lib/async-functions/setIntervalIfPossible";
import { getIsBrandPreview } from "@faire/retailer/lib/getIsBrandPreview";
import { isE2E } from "@faire/retailer/lib/isE2E";
import { isLoggedInRetailer } from "@faire/retailer/lib/isLoggedInRetailer";
import { AlertBannerStore } from "@faire/retailer/packages/core/services/AlertBanner/AlertBannerStore";
import { AppHistory } from "@faire/retailer/packages/core/services/AppHistory";
import { getBrand } from "@faire/retailer/serialized-data/getBrand";
import { getBrandDiscounts } from "@faire/retailer/serialized-data/getBrandDiscounts";
import { getFaireDirectMarketplaceDiscountData } from "@faire/retailer/serialized-data/getFaireDirectMarketplaceDiscountData";
import { getInsiderMemershipInformation } from "@faire/retailer/serialized-data/getInsiderMembershipInformation";
import { getSettingEnableKnownReferrerAdoption } from "@faire/retailer/settings/getSettingEnableKnownReferrerAdoption";
import { specialHolidayNet90Active } from "@faire/retailer/settings/getSettingSpecialHolidayNet90";
import { MarketStore } from "@faire/retailer/stores/domain/Events/FaireMarkets/MarketStore";
import { FirstOrderIncentiveStore } from "@faire/retailer/stores/domain/FirstOrderIncentiveStore";
import { FreeShippingStore } from "@faire/retailer/stores/domain/FreeShippingStore";
import { MilestonesStore } from "@faire/retailer/stores/domain/MilestonesStore";
import { OpeningOrderIncentivesStore } from "@faire/retailer/stores/domain/OpeningOrderIncentivesStore";
import { OpenWithFaireStore } from "@faire/retailer/stores/domain/OpenWithFaireStore";
import { RetailerStore } from "@faire/retailer/stores/domain/RetailerStore";
import { UserStore } from "@faire/retailer/stores/domain/UserStore";

const ROUTE_BLACKLIST = [
  brandApplicationRoute,
  brandAdApplicationRoute,
  messagesRoute,
  messagesWithOtherPartyRoute,
  brandInvoiceTokenRoute,
  brandReferralsRoute,
  posConsentRoute,
];

/**
 * @deprecated prefer to use `usePromoBannerStore` or `withStores` instead
 */
export class PromoBannerStore {
  /**
   * @trackfunction
   */
  static get = singletonGetter(PromoBannerStore);
  static bannerIntervalMs: number = 7500;

  @observable
  bannerHeight: number = 0;

  @observable
  private currentTime: number = clock.now();
  private interval?: number;

  @observable
  private inProductMessagingBannersList:
    | IProductMessageByViewport[]
    | undefined;

  constructor() {
    makeObservable(this);
    this.initialize();
  }

  @action
  private initialize = () => {
    // eslint-disable-next-line @faire/ssr-no-restricted-globals
    if (typeof window !== "undefined" && !dangerouslyGetIsSSR()) {
      this.interval = setIntervalIfPossible(this.timer.bind(this), 1000);
    }
  };

  @action
  private timer = () => {
    this.setCurrentTime(clock.now());

    // Cancel the timer if it no longer needs to run.
    if (
      this.interval &&
      !this.showingEmployeeGrantedFreeFreightBanner &&
      this.hasPlacedFirstOrder &&
      !this.showMarketCountdownBanner
    ) {
      clearInterval(this.interval);
    }
  };

  @action
  private setCurrentTime = (currentTime: number) => {
    this.currentTime = currentTime;
  };

  @action
  setBannerHeight = (height: number) => {
    this.bannerHeight = height;
  };

  @computed
  get bannerRotationSpeed(): number {
    return PromoBannerStore.bannerIntervalMs;
  }

  @computed
  get visibleHeight() {
    if (!this.isVisible) {
      return 0;
    }

    return Math.max(this.bannerHeight - Viewport.getDefault().scrollY, 0);
  }

  @computed
  get isVisible() {
    return this.banners.length > 0;
  }

  @computed
  private get isAppMobileWebView(): boolean {
    const queryParams = parseQueryParams(getLocationOrThrow().search);
    return queryParams.get("native_env") === "true";
  }

  @computed
  get banners(): PromoBannerType[] {
    // FD-114601: Most banners should not be visible in app mobile view
    //   to prevent user from escaping into the rest of website
    if (this.isBlacklisted || this.isAppMobileWebView) {
      return [];
    }

    // Brand preview banner, should override any banner meant for retailers
    if (this.showBrandPreviewBanner) {
      return [PromoBannerType.BRAND_PREVIEW];
    }

    if (
      this.shouldPrioritizeReferralFollowupBanner &&
      !this.showFaireDirectIncentivesBanner
    ) {
      return [PromoBannerType.REFERRAL_FOLLOWUP];
    }

    if (
      this.shouldPrioritizeKnownReferralAdoptionBanner &&
      !this.showFaireDirectIncentivesBanner
    ) {
      return [PromoBannerType.KNOWN_REFERRER_ADOPTION];
    }

    const applicableBanners = isLoggedInRetailer()
      ? LOGGED_IN_PROMO_BANNERS
      : LOGGED_OUT_PROMO_BANNERS;
    const visibleBanners = applicableBanners
      .map((types) =>
        Array.isArray(types) ? types.filter(this.isBannerVisible) : types
      )
      .filter((types) =>
        Array.isArray(types) ? types.length > 0 : this.isBannerVisible(types)
      );

    if (visibleBanners.length === 0) {
      return [];
    }

    const shownBanners = Array.isArray(visibleBanners[0])
      ? visibleBanners[0]
      : [visibleBanners[0]];

    return compact(shownBanners);
  }

  private isBannerVisible = (type: PromoBannerType): boolean => {
    switch (type) {
      case PromoBannerType.FAIRE_DIRECT_INCENTIVES:
        return this.showFaireDirectIncentivesBanner;
      case PromoBannerType.MARKET_COUNTDOWN:
        return this.showMarketCountdownBanner;
      case PromoBannerType.FAIRE_VALUES:
        return this.showFaireValuesBanner;
      case PromoBannerType.FIRST_ORDER_INCENTIVE:
        return FirstOrderIncentiveStore.get()
          .shouldShowFirstOrderIncentiveBanner;
      case PromoBannerType.INSIDER_AUTO_ENROLL:
        return this.showInsiderAutoEnrollBanner;
      case PromoBannerType.SPECIAL_HOLIDAY_NET90:
        return this.showSpecialHolidayNet90;
      case PromoBannerType.OPENING_ORDER_INCENTIVES:
        return this.showOpeningOrderIncentivesBanner;
      case PromoBannerType.AMEX_INCENTIVE:
        return this.showAmexBanner;
      case PromoBannerType.BRAND_PREVIEW:
        return this.showBrandPreviewBanner;
      case PromoBannerType.BRANDS_COUNTER:
        return this.showingBrandsCounterBanner;
      case PromoBannerType.WAREHOUSE_SALE:
        return this.showWarehouseSaleBanner;
      case PromoBannerType.EMPLOYEE_GRANTED_FREE_FREIGHT:
        return this.showingEmployeeGrantedFreeFreightBanner;
      case PromoBannerType.OPEN_WITH_FAIRE:
        return this.showOpenWithFaireEligibleToApplyBanner;
      case PromoBannerType.POS_INTEGRATION:
        return this.showPosIntegrationBanner;
      case PromoBannerType.REFERRAL_FOLLOWUP:
        return this.showReferralFollowupBanner;
      case PromoBannerType.KNOWN_REFERRER_ADOPTION:
        return this.showKnownReferralAdoptionBanner;
      default:
        return false;
    }
  };

  @computed
  get showingBrandsCounterBanner(): boolean {
    return (
      !UserStore.get().user &&
      !(
        pathMatchesRoute(getCurrentPath(), productRoute) ||
        pathMatchesRoute(getCurrentPath(), collectionRoute)
      )
    );
  }

  @computed
  private get hasBrandsReferralWithBonusOffers(): boolean {
    const bonusOffersReferralNamesList = getGlobalProperty<Array<string>>(
      "referralBonusOfferReferralNames"
    );
    return (
      !!bonusOffersReferralNamesList && bonusOffersReferralNamesList.length > 0
    );
  }

  @computed
  private get showReferralFollowupBanner(): boolean {
    const pathName = AppHistory.get().location?.pathname;
    const isOnReferralPage = matchPathWithLocale(
      pathName ?? "",
      brandReferralRoute
    );
    const hasSentReferralBonusFollowup =
      MilestonesStore.get().hasSentFirstReferralBonusFollowup;
    return (
      !isOnReferralPage &&
      !hasSentReferralBonusFollowup &&
      this.hasBrandsReferralWithBonusOffers
    );
  }

  @computed
  private get shouldPrioritizeReferralFollowupBanner(): boolean {
    // Prioritize referral followup banner over other banners for the first 5 days.
    return (
      MilestonesStore.get().countReferralFollowupBonusBannerSeen <= 5 &&
      this.showReferralFollowupBanner
    );
  }

  @computed
  private get hasKnownReferralAdoptionNudges(): boolean {
    const brandsToNudgeNamesList = getGlobalProperty<Array<string>>(
      "knownReferrerAdoptionBrandNames"
    );
    return !!brandsToNudgeNamesList && brandsToNudgeNamesList.length > 0;
  }

  @computed
  private get showKnownReferralAdoptionBanner(): boolean {
    const pathName = AppHistory.get().location?.pathname;
    const isOnReferralPage = matchPathWithLocale(
      pathName ?? "",
      brandReferralRoute
    );
    const hasSentReferralBonusFollowup =
      MilestonesStore.get().hasSentFirstKnownReferrerAdoptionEmail;
    return (
      !isOnReferralPage &&
      !hasSentReferralBonusFollowup &&
      this.hasKnownReferralAdoptionNudges &&
      getSettingEnableKnownReferrerAdoption()
    );
  }

  @computed
  private get shouldPrioritizeKnownReferralAdoptionBanner(): boolean {
    // Prioritize known referrer adoption banner over other banners for the first 5 days.
    return (
      MilestonesStore.get().countKnownReferrerAdoptionBannerSeen <= 5 &&
      this.showKnownReferralAdoptionBanner
    );
  }

  @observable
  private posSummaryResponse = lazyObservable<
    IRetailerPosLinkSummary | undefined
  >((sink) => {
    if (!isE2E() && !dangerouslyGetIsSSR()) {
      posGetSummary().then((response) => sink(response.pos_link_summary));
    }
  });

  @computed
  private get showPosIntegrationBanner(): boolean {
    const firstOrder = RetailerStore.get().retailer?.first_order_at;
    const now = clock.now();
    const posSummary = _allowStateChangesInsideComputed(() =>
      this.posSummaryResponse.current()
    );
    // We should read hasLinkedPOS from usePOSSummary once this store is refactored
    const hasLinkedPOS =
      posSummary?.retailer_link_state ===
        RetailerLinkState.RETAILER_LINK_LINKED &&
      (posSummary?.onboarding_progress ===
        RetailerOnboardingProgress.V2_FINISHED ||
        posSummary?.onboarding_progress ===
          RetailerOnboardingProgress.FINISHED);

    return (
      isNotUndefined(firstOrder) &&
      differenceInDays(now, firstOrder) >= 1 &&
      differenceInDays(now, firstOrder) < 10 &&
      !hasLinkedPOS
    );
  }

  @computed
  private get showBrandPreviewBanner(): boolean {
    return getIsBrandPreview() && !getBrand()?.first_active_at;
  }

  @computed
  get showFaireValuesBanner(): boolean {
    return (
      (isLoggedInRetailer() &&
        // we only want to show this to retailer created after May 06, 2021
        !isBefore(
          RetailerStore.get().retailer?.created_at ?? 0,
          1620270000000
        ) &&
        this.hasFirstOrderIncentive) ||
      !!this.inProductMessagingBanners?.length
    );
  }

  @computed
  get showingEmployeeGrantedFreeFreightBanner() {
    if (isUndefined(this.employeeGrantedFreeFreightDeadline)) {
      return false;
    }
    return isBefore(this.currentTime, this.employeeGrantedFreeFreightDeadline);
  }

  @computed
  get showOpenWithFaireEligibleToApplyBanner(): boolean {
    return OpenWithFaireStore.get().showOwFEntrypointEligibleOnly;
  }

  @computed
  private get hasPlacedFirstOrder(): boolean {
    return !!RetailerStore.get().retailer?.first_order_at;
  }

  private getExpiryDateForFreeShippingReason = (reason: FreeShippingReason) => {
    const freeShippingStatus =
      FreeShippingStore.get().freeShippingStatuses?.find(
        (status) => status.reason === reason
      );
    if (!freeShippingStatus || !freeShippingStatus.expiry) {
      return;
    }
    return new Date(freeShippingStatus.expiry);
  };

  @computed
  get employeeGrantedFreeFreightDeadline() {
    return this.getExpiryDateForFreeShippingReason(
      FreeShippingReason.EMPLOYEE_GRANTED_FREE_SHIPPING
    );
  }

  @computed
  private get isBlacklisted() {
    const getCurrentPath = AppHistory.get().location?.pathname ?? "";
    return (
      some(ROUTE_BLACKLIST, (route) =>
        pathMatchesRoute(getCurrentPath, route)
      ) ||
      (pathMatchesRoute(getCurrentPath, brandPageRoute) &&
        isLoggedInRetailer() &&
        Viewport.getDefault().isMobile)
    );
  }

  @computed
  private get hasFirstOrderIncentive(): boolean {
    return FirstOrderIncentiveStore.get().shouldShowFirstOrderIncentiveBanner;
  }

  @computed
  private get showAmexBanner(): boolean {
    return (
      RetailerStore.get().showAmexBanner ||
      pathMatchesRoute(getCurrentPath(), amexRoute)
    );
  }

  @computed
  private get isAlertBannerVisible() {
    return AlertBannerStore.get().banner?.visible;
  }

  @computed
  get backgroundColor() {
    return this.isAlertBannerVisible
      ? Core.surface.inverse
      : Core.surface.default;
  }

  @computed
  get textColor() {
    return this.isAlertBannerVisible ? Core.text.default : Core.text.inverse;
  }

  @computed
  private get showMarketCountdownBanner() {
    return (
      MarketStore.get().showCountdownBanner &&
      // When market banner should show, but retailer has First
      //   Order Incentive, make the Faire Values rotating banner top priority.
      // https://www.notion.so/faire/2023-FSM-New-Retailer-Proposal-a0a81622db7841a29e63099fcb832ba8
      !this.hasFirstOrderIncentive
    );
  }

  @computed
  private get showSpecialHolidayNet90(): boolean {
    return (
      !this.showOpenWithFaireEligibleToApplyBanner &&
      specialHolidayNet90Active()
    );
  }

  @computed
  get showInsiderAutoEnrollBanner(): boolean {
    return (
      getInsiderMemershipInformation()?.state === "AUTO_ENROLLED_TRIAL" &&
      (getInsiderMemershipInformation()?.current_period_end_at ?? 0) >
        Date.now()
    );
  }

  @computed
  private get showWarehouseSaleBanner(): boolean {
    return RetailerStore.get().showWarehouseFlashSale;
  }

  @computed
  private get showOpeningOrderIncentivesBanner(): boolean {
    return (
      !!OpeningOrderIncentivesStore.get().showOpeningOrderIncentives &&
      !!OpeningOrderIncentivesStore.get().openingOrderIncentivesProps
    );
  }

  @computed
  private get showFaireDirectIncentivesBanner(): boolean {
    return (
      hasFaireDirectFirstOrderIncentive(this.faireDirectFirstOrderIncentive) ||
      hasFaireDirectMarketplaceIncentive(
        this.faireDirectMarketplaceIncentive,
        isLoggedInRetailer()
      )
    );
  }

  @observable
  private faireDirectFirstOrderIncentive =
    parseFaireDirectFirstOrderIncentiveFromBrandDiscounts(
      getBrandDiscounts()?.brand_discounts ?? {}
    );

  @observable
  private faireDirectMarketplaceIncentive =
    getFaireDirectMarketplaceDiscountData();

  @action
  setFaireDirectFirstOrderIncentive = (
    discount: Partial<Record<string, IBrandDiscount>> | undefined
  ) => {
    if (!discount) {
      return;
    }

    this.faireDirectFirstOrderIncentive =
      parseFaireDirectFirstOrderIncentiveFromBrandDiscounts(discount);
  };

  @action
  setFaireDirectMarketplaceIncentive = (
    discount: IFDMarketplaceDiscountData | undefined
  ) => {
    if (!discount) {
      return;
    }

    this.faireDirectMarketplaceIncentive = discount;
  };

  @action
  setInProductMessagingBanners = (value: IProductMessageByViewport[]) => {
    this.inProductMessagingBannersList = value;
  };

  @computed
  get inProductMessagingBanners() {
    if (!this.inProductMessagingBannersList?.length) {
      return;
    }

    return this.inProductMessagingBannersList;
  }
}

export const usePromoBannerStore = createStoreHook(PromoBannerStore);
